import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store/store';
import { setMessage, submitFilledQuestionnaire } from '../../store/actions/filled-questionnaire-action';

const LeaveNote: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [localMessage, setLocalMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isTablet, setIsTablet] = useState(window.innerWidth > 768 && window.innerWidth <= 1024);

  const filledQuestionnaire = useSelector((state: RootState) => state.filledQuestionnaire);
  const sections = useSelector((state: RootState) => state.questionnaire.sections);
  
  // Track current section and phase indices
  const storedSectionIndex = useSelector((state: RootState) => state.filledQuestionnaire.currentSectionIndex);
  const storedPhaseIndex = useSelector((state: RootState) => state.filledQuestionnaire.currentPhaseIndex);
  
  const [sectionIndex] = useState<number>(storedSectionIndex || 0);
  const [phaseIndex] = useState<number>(storedPhaseIndex || 0);

  // Get the last section for display
  const lastSection = sections && sections.length > 0 ? sections[sections.length - 1] : null;
  
  // Get current section and phase
  const currentSection = sections[sectionIndex];
  
  // Get phases for the current section only
  const getCurrentSectionPhases = () => {
    if (!currentSection || !currentSection.phases) return [];
    return currentSection.phases.map((phase: any, idx: number) => ({
      ...phase,
      phaseIndex: idx
    }));
  };

  const currentSectionPhases = getCurrentSectionPhases();

  useEffect(() => {
    // Check if there is questionnaire data
    if (!filledQuestionnaire.questionnairesId || filledQuestionnaire.filledSections.length === 0) {
      navigate('/');
    }
  }, [filledQuestionnaire, navigate]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsTablet(window.innerWidth > 768 && window.innerWidth <= 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleBack = () => {
    navigate('/questionnaire');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setIsSubmitting(true);

    try {
      // Set message in Redux state if provided
      if (localMessage.trim()) {
        dispatch(setMessage(localMessage) as any);
      }

      // Submit the filled questionnaire
      await dispatch(submitFilledQuestionnaire() as any);

      // Navigate to thank you page immediately
      // Note: Don't reset state here - it will be reset when user leaves thank you page
      navigate('/thank-you-page');
    } catch (error) {
      console.error('Error submitting questionnaire:', error);
      alert('Failed to submit. Please try again.');
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <style>{`
        body, html {
          margin: 0;
          padding: 0;
          background-color: #1C1D1F !important;
          overflow-x: hidden;
        }
        * {
          box-sizing: border-box;
        }

        /* Custom checkbox styles */
        input[type="checkbox"] {
          appearance: none;
          -webkit-appearance: none;
          width: 20px;
          height: 20px;
          min-width: 20px;
          border: 1px solid #7A7B8A;
          border-radius: 4px;
          background: transparent;
          cursor: pointer;
          position: relative;
          transition: all 0.2s ease;
          margin-top: 2px;
        }

        input[type="checkbox"]:checked {
          background: #249FDA;
          border-color: #249FDA;
        }

        input[type="checkbox"]:checked::after {
          content: '';
          position: absolute;
          left: 6px;
          top: 2px;
          width: 6px;
          height: 10px;
          border: solid white;
          border-width: 0 2px 2px 0;
          transform: rotate(45deg);
        }

        /* Textarea resize handle */
        .resize-handle {
          position: absolute;
          bottom: 8px;
          right: 8px;
          width: 24px;
          height: 24px;
          pointer-events: none;
          opacity: 0.5;
        }

        /* Loading spinner animation */
        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }

        .loading-spinner {
          border: 2px solid #ffffff40;
          border-top: 2px solid #ffffff;
          border-radius: 50%;
          width: 16px;
          height: 16px;
          animation: spin 1s linear infinite;
          margin-right: 8px;
        }

        /* Fade animation for loading text */
        @keyframes fadeInOut {
          0%, 100% {
            opacity: 0.6;
          }
          50% {
            opacity: 1;
          }
        }

        .loading-text {
          animation: fadeInOut 1.5s ease-in-out infinite;
        }
      `}</style>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: isMobile ? '20px' : isTablet ? '40px' : '64px',
          gap: '40px',
          minHeight: '100vh',
          width: '100vw',
          backgroundColor: '#1C1D1F',
          boxSizing: 'border-box',
          position: 'relative',
          left: '0',
          top: '0'
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: (isMobile || isTablet) ? 'column' : 'row',
            alignItems: (isMobile || isTablet) ? 'center' : 'flex-start',
            padding: '0px',
            gap: isMobile ? '40px' : isTablet ? '56px' : '72px',
            width: '100%',
            maxWidth: '1260px'
          }}
        >
          {/* Left Column - Content */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: (isMobile || isTablet) ? 'center' : 'flex-start',
              justifyContent: 'space-between',
              padding: '0px',
              width: (isMobile || isTablet) ? '100%' : '573px',
              height: 'auto',
              minHeight: isMobile ? '400px' : '600px',
              flex: 'none',
              order: 0,
              flexGrow: 1,
              textAlign: (isMobile || isTablet) ? 'center' : 'left'
            }}
          >
            {/* Header section */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: (isMobile || isTablet) ? 'center' : 'flex-start',
                padding: '0px',
                gap: isMobile ? '24px' : isTablet ? '40px' : '50px',
                width: '100%',
                maxWidth: '573px',
                flex: 'none',
                order: 0,
                alignSelf: 'stretch',
                flexGrow: 0
              }}
            >
              <h1
                style={{
                  width: '100%',
                  fontFamily: 'Archivo',
                  fontStyle: 'normal',
                  fontWeight: 700,
                  fontSize: isMobile ? '28px' : isTablet ? '36px' : '40px',
                  lineHeight: '130%',
                  color: '#EAEEF4',
                  margin: 0,
                  textAlign: (isMobile || isTablet) ? 'center' : 'left'
                }}
              >
                Leave us a message (Optional)
              </h1>
              <p
                style={{
                  width: '100%',
                  fontFamily: 'Inter',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  fontSize: isMobile ? '14px' : isTablet ? '15px' : '16px',
                  lineHeight: '150%',
                  fontFeatureSettings: "'calt' off",
                  color: '#A7A9BE',
                  margin: 0,
                  textAlign: (isMobile || isTablet) ? 'center' : 'left'
                }}
              >
                Before you submit your information, you can leave us an additional message or note if you wish.
              </p>
            </div>

            {/* Middle section - Phase buttons */}
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: (isMobile || isTablet) ? 'center' : 'flex-start',
                    padding: '0px',
                    gap: '12px',
                    width: isMobile ? '150px' : isTablet ? '160px' : '172px',
                    maxHeight: isMobile ? '200px' : '300px',
                    overflowY: 'auto',
                    flex: 'none',
                    order: 1,
                    flexGrow: 0
                }}
            >
                {currentSectionPhases.map((phase: any, index: number) => {
                    const isCompleted = index < phaseIndex;
                    const isCurrent = index === phaseIndex;
                    const isAccessible = isCompleted || isCurrent;

                    return (
                        <div
                            key={`${sectionIndex}-${index}`}
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'center',
                                alignItems: 'center',
                                padding: '6px 12px',
                                gap: '8px',
                                width: isMobile ? '150px' : isTablet ? '160px' : '172px',
                                minHeight: '32px',
                                background: isCurrent ? '#36363A' : isCompleted ? '#2A2A2D' : 'transparent',
                                border: isAccessible ? 'none' : '1px solid #7A7B8A',
                                borderRadius: '20px',
                                flex: 'none',
                                order: index,
                                flexGrow: 0,
                                cursor: 'default',
                                opacity: isCurrent ? 1 : isCompleted ? 0.8 : 0.5
                            }}
                        >
                            <span
                                style={{
                                    fontFamily: 'Inter',
                                    fontStyle: 'normal',
                                    fontWeight: 400,
                                    fontSize: isMobile ? '13px' : '14px',
                                    lineHeight: '140%',
                                    textAlign: 'center',
                                    fontFeatureSettings: "'calt' off",
                                    color: isAccessible ? '#FFFFFF' : '#A7A9BE',
                                    flex: 'none',
                                    order: 0,
                                    flexGrow: 0
                                }}
                            >
                                {phase?.name || `Phase ${index + 1}`}
                            </span>
                        </div>
                    );
                })}
            </div>

            {/* Bottom section - Social media icons */}
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: (isMobile || isTablet) ? 'center' : 'flex-start',
                    alignItems: 'center',
                    padding: '0px',
                    gap: '15px',
                    width: '141px',
                    height: '24px',
                    flex: 'none',
                    order: 2,
                    flexGrow: 0
                }}
            >
                {/* Facebook */}
                <a href="https://www.facebook.com/SmartCat.io" target="_blank" rel="noopener noreferrer" style={{ width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg width="10" height="19" viewBox="0 0 10 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9.5 3.5H6.5C5.9477 3.5 5.5 3.94772 5.5 4.5V7.5H9.5C9.6137 7.49748 9.7216 7.5504 9.7892 7.6419C9.8568 7.7334 9.8758 7.852 9.84 7.96L9.1 10.16C9.0318 10.3619 8.8431 10.4984 8.63 10.5H5.5V18C5.5 18.2761 5.2761 18.5 5 18.5H2.5C2.22386 18.5 2 18.2761 2 18V10.5H0.5C0.22386 10.5 0 10.2761 0 10V8C0 7.7239 0.22386 7.5 0.5 7.5H2V4.5C2 2.29086 3.7909 0.5 6 0.5H9.5C9.7761 0.5 10 0.72386 10 1V3C10 3.27614 9.7761 3.5 9.5 3.5Z" fill="#A7A9BE"/>
                  </svg>
                </a>

                {/* Instagram */}
                <a href="https://www.instagram.com/smartcat.io/" target="_blank" rel="noopener noreferrer" style={{ width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg width="18" height="19" viewBox="0 0 18 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" clipRule="evenodd" d="M13 0.5H5C2.23858 0.5 0 2.73858 0 5.5V13.5C0 16.2614 2.23858 18.5 5 18.5H13C15.7614 18.5 18 16.2614 18 13.5V5.5C18 2.73858 15.7614 0.5 13 0.5ZM16.25 13.5C16.2445 15.2926 14.7926 16.7445 13 16.75H5C3.20735 16.7445 1.75549 15.2926 1.75 13.5V5.5C1.75549 3.70735 3.20735 2.25549 5 2.25H13C14.7926 2.25549 16.2445 3.70735 16.25 5.5V13.5ZM13.75 5.75C14.3023 5.75 14.75 5.30228 14.75 4.75C14.75 4.19772 14.3023 3.75 13.75 3.75C13.1977 3.75 12.75 4.19772 12.75 4.75C12.75 5.30228 13.1977 5.75 13.75 5.75ZM9 5C6.51472 5 4.5 7.01472 4.5 9.5C4.5 11.9853 6.51472 14 9 14C11.4853 14 13.5 11.9853 13.5 9.5C13.5027 8.3057 13.0294 7.15957 12.1849 6.31508C11.3404 5.47059 10.1943 4.99734 9 5ZM6.25 9.5C6.25 11.0188 7.4812 12.25 9 12.25C10.5188 12.25 11.75 11.0188 11.75 9.5C11.75 7.9812 10.5188 6.75 9 6.75C7.4812 6.75 6.25 7.9812 6.25 9.5Z" fill="#A7A9BE"/>
                  </svg>
                </a>

                {/* The X */}
                <a href="https://x.com/SmartCat_io" target="_blank" rel="noopener noreferrer" style={{ width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg width="18" height="19" viewBox="0 0 18 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17.1328 17.3478L11.264 8.12469L17.0549 1.75438C17.1859 1.60674 17.2534 1.41343 17.2428 1.21636C17.2322 1.01929 17.1444 0.834346 16.9983 0.701615C16.8523 0.568884 16.6598 0.49907 16.4626 0.507309C16.2654 0.515547 16.0794 0.601174 15.9449 0.745625L10.4287 6.81312L6.63276 0.847813C6.56507 0.74127 6.47158 0.653535 6.36097 0.592735C6.25035 0.531936 6.12617 0.500039 5.99994 0.5H1.49994C1.36547 0.499935 1.23345 0.536026 1.11771 0.604494C1.00198 0.672962 0.906774 0.771288 0.842079 0.889177C0.777384 1.00707 0.745575 1.14018 0.749983 1.27458C0.75439 1.40898 0.794852 1.53973 0.867132 1.65313L6.73588 10.8753L0.944945 17.2503C0.87732 17.323 0.824785 17.4083 0.790386 17.5014C0.755987 17.5945 0.740407 17.6935 0.74455 17.7927C0.748693 17.8918 0.772477 17.9892 0.814522 18.0791C0.856566 18.169 0.916036 18.2496 0.989485 18.3164C1.06293 18.3831 1.1489 18.4346 1.2424 18.4679C1.3359 18.5012 1.43508 18.5156 1.53419 18.5103C1.63329 18.5049 1.73035 18.48 1.81974 18.4369C1.90914 18.3937 1.98908 18.3333 2.05494 18.2591L7.5712 12.1916L11.3671 18.1569C11.4354 18.2625 11.5291 18.3494 11.6397 18.4093C11.7503 18.4693 11.8742 18.5005 11.9999 18.5H16.4999C16.6343 18.5 16.7661 18.4638 16.8817 18.3954C16.9973 18.327 17.0924 18.2288 17.1571 18.111C17.2218 17.9933 17.2536 17.8604 17.2493 17.7261C17.2451 17.5918 17.2048 17.4612 17.1328 17.3478ZM12.4115 17L2.86588 2H5.58463L15.134 17H12.4115Z" fill="#A7A9BE"/>
                  </svg>
                </a>

                {/* LinkedIn */}
                <a href="https://www.linkedin.com/company/smartcat-io/" target="_blank" rel="noopener noreferrer" style={{ width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg width="18" height="19" viewBox="0 0 18 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" clipRule="evenodd" d="M2 0.5H16C17.1046 0.5 18 1.39543 18 2.5V16.5C18 17.6046 17.1046 18.5 16 18.5H2C0.89543 18.5 0 17.6046 0 16.5V2.5C0 1.39543 0.89543 0.5 2 0.5ZM5 15.5C5.27614 15.5 5.5 15.2761 5.5 15V8C5.5 7.7239 5.27614 7.5 5 7.5H3.5C3.22386 7.5 3 7.7239 3 8V15C3 15.2761 3.22386 15.5 3.5 15.5H5ZM4.25 6.5C3.42157 6.5 2.75 5.82843 2.75 5C2.75 4.17157 3.42157 3.5 4.25 3.5C5.07843 3.5 5.75 4.17157 5.75 5C5.75 5.82843 5.07843 6.5 4.25 6.5ZM14.5 15.5C14.7761 15.5 15 15.2761 15 15V10.4C15.0325 8.8108 13.8576 7.45452 12.28 7.26C11.177 7.15925 10.1083 7.6744 9.5 8.6V8C9.5 7.7239 9.2761 7.5 9 7.5H7.5C7.2239 7.5 7 7.7239 7 8V15C7 15.2761 7.2239 15.5 7.5 15.5H9C9.2761 15.5 9.5 15.2761 9.5 15V11.25C9.5 10.4216 10.1716 9.75 11 9.75C11.8284 9.75 12.5 10.4216 12.5 11.25V15C12.5 15.2761 12.7239 15.5 13 15.5H14.5Z" fill="#A7A9BE"/>
                  </svg>
                </a>
            </div>
          </div>

          {/* Right Column - Message Form (Replacing Questions Form) */}
          <form onSubmit={handleSubmit}
            noValidate
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              padding: isMobile ? '24px' : isTablet ? '32px' : '40px',
              margin: '0 0 40px 0',
              width: (isMobile || isTablet) ? '100%' : '615px',
              maxWidth: isTablet ? '600px' : 'none',
              height: 'auto',
              minHeight: isMobile ? '400px' : '600px',
              background: '#27272A',
              borderRadius: '16px',
              flex: 'none',
              order: 1,
              flexGrow: 0,
              boxSizing: 'border-box',
              justifyContent: 'space-between',
              gap: isMobile ? '30px' : '40px'
            }}
          >
            {/* Form Content */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: isMobile ? '20px' : '24px',
              width: '100%'
            }}>
              {/* Message Field */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                padding: '0px',
                gap: '8px',
                width: '100%'
              }}>
                <label
                  htmlFor="message"
                  style={{
                    fontFamily: 'Inter',
                    fontStyle: 'normal',
                    fontWeight: 400,
                    fontSize: '14px',
                    lineHeight: '150%',
                    fontFeatureSettings: "'calt' off",
                    color: '#A7A9BE',
                    flex: 'none',
                    order: 0,
                    flexGrow: 0
                  }}
                >
                  Message
                </label>
                <div style={{ position: 'relative', width: '100%' }}>
                  <textarea
                    id="message"
                    placeholder="Type here..."
                    value={localMessage}
                    onChange={(e) => setLocalMessage(e.target.value)}
                    style={{
                      boxSizing: 'border-box',
                      width: '100%',
                      minHeight: isMobile ? '120px' : '160px',
                      background: '#36363A',
                      border: '1px solid #36363A',
                      borderRadius: '8px',
                      padding: isMobile ? '12px' : '16px',
                      fontFamily: 'Inter',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      fontSize: isMobile ? '14px' : isTablet ? '15px' : '16px',
                      lineHeight: '150%',
                      fontFeatureSettings: "'calt' off",
                      color: '#FFFFFF',
                      resize: 'vertical',
                      outline: 'none'
                    }}
                  />
                  <div className="resize-handle">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="6" cy="18" r="2" fill="#A7A9BE"/>
                      <circle cx="12" cy="18" r="2" fill="#A7A9BE"/>
                      <circle cx="12" cy="12" r="2" fill="#A7A9BE"/>
                      <circle cx="18" cy="6" r="2" fill="#A7A9BE"/>
                      <circle cx="18" cy="18" r="2" fill="#A7A9BE"/>
                      <circle cx="18" cy="12" r="2" fill="#A7A9BE"/>
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            {/* Divider and Buttons Container */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: isMobile ? '20px' : '24px',
              width: '100%'
            }}>
              {/* Divider */}
              <div style={{
                width: '100%',
                height: '0px',
                border: '1px solid #36363A'
              }} />

              {/* Buttons */}
              <div style={{
                display: 'flex',
                flexDirection: (isMobile || isTablet) ? 'column' : 'row',
                justifyContent: 'flex-end',
                alignItems: 'center',
                gap: isMobile ? '12px' : isTablet ? '14px' : '16px',
                width: '100%'
              }}>
              <button
                type="button"
                onClick={handleBack}
                disabled={isSubmitting}
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: isMobile ? '10px 24px' : isTablet ? '11px 28px' : '12px 32px',
                  width: (isMobile || isTablet) ? '100%' : 'auto',
                  height: isMobile ? '44px' : isTablet ? '46px' : '48px',
                  background: '#36363A',
                  backdropFilter: 'blur(4px)',
                  borderRadius: '8px',
                  border: 'none',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  opacity: isSubmitting ? 0.5 : 1,
                  fontFamily: 'Inter',
                  fontStyle: 'normal',
                  fontWeight: 500,
                  fontSize: isMobile ? '14px' : isTablet ? '15px' : '16px',
                  lineHeight: '150%',
                  fontFeatureSettings: "'calt' off",
                  color: '#FFFFFF'
                }}
              >
                Go Back
              </button>

              <button
                type="submit"
                disabled={isSubmitting}
                style={{
                  boxSizing: 'border-box',
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: isMobile ? '10px 24px' : isTablet ? '11px 28px' : '12px 32px',
                  width: (isMobile || isTablet) ? '100%' : 'auto',
                  height: isMobile ? '44px' : isTablet ? '46px' : '48px',
                  background: isSubmitting ? '#36363A' : 'linear-gradient(91.9deg, #145F96 0.8%, #249FDA 99.45%)',
                  borderRadius: '8px',
                  border: 'none',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  opacity: isSubmitting ? 0.5 : 1,
                  fontFamily: 'Inter',
                  fontStyle: 'normal',
                  fontWeight: 500,
                  fontSize: isMobile ? '14px' : isTablet ? '15px' : '16px',
                  lineHeight: '150%',
                  fontFeatureSettings: "'calt' off",
                  color: '#FFFFFF',
                  transition: 'all 0.3s ease'
                }}
              >
                {isSubmitting ? (
                  <>
                    <div className="loading-spinner" />
                    <span className="loading-text">Submitting...</span>
                  </>
                ) : (
                  'Submit Form'
                )}
              </button>
            </div>
          </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default LeaveNote;